/**
 * FishMapHallOfFamePage — public credit page for every approved
 * crowdsource submission to the Hustle Fish Map.
 *
 * Routes:  /fish-map/hall-of-fame
 *
 * Each card lists the submitter's name (or "Anonymous angler"),
 * the species, the location, and a deep-link back to the live spot
 * on the map.  Leaderboard sidebar tracks top contributors +
 * most-submitted species.
 *
 * SEO win: pre-renders long-tail queries like "barra spots near
 * Awoonga" and "mud crab Tin Can Bay" with a structured anchor.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Trophy, MapPin, Fish, Droplet, Waves, ArrowLeft, Loader2 } from "lucide-react";
import TackleLogo from "../components/tackle/TackleLogo";
import "./FishMapHallOfFamePage.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

const STATE_LABEL = {
  QLD: "Queensland", NSW: "New South Wales", VIC: "Victoria",
  WA: "Western Australia", SA: "South Australia", TAS: "Tasmania",
  NT: "Northern Territory", ACT: "ACT",
};

export default function FishMapHallOfFamePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState("");
  const [water, setWater] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (state) params.set("state", state);
    if (water) params.set("water", water);
    api.get(`/api/fish-map/hall-of-fame?${params.toString()}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ total: 0, results: [], leaderboard: [], top_species: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [state, water]);

  const grouped = useMemo(() => {
    if (!data?.results) return [];
    const m = new Map();
    for (const r of data.results) {
      const key = r.state || "Other";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    }
    return Array.from(m.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [data]);

  return (
    <div className="hof-root" data-testid="fish-map-hall-of-fame-root">
      {/* Header */}
      <header className="hof-header">
        <TackleLogo />
        <Link to="/fish-map" className="hof-back" data-testid="hof-back">
          <ArrowLeft size={14} /> Back to Fish Map
        </Link>
      </header>

      {/* Hero */}
      <section className="hof-hero">
        <div className="hof-hero__badge">
          <Trophy size={14} /> Fish Map Hall of Fame
        </div>
        <h1 className="hof-hero__title">
          The anglers who built the map.
        </h1>
        <p className="hof-hero__sub">
          Every spot on the Hustle Fish Map that&apos;s been crowdsourced —
          credited to the local angler who shared it. If you know a spot we
          don&apos;t,&nbsp;
          <Link to="/fish-map" className="hof-hero__link">submit it here</Link>.
        </p>
        <div className="hof-stats">
          <div className="hof-stat">
            <div className="hof-stat__n" data-testid="hof-total-spots">
              {data?.total ?? "—"}
            </div>
            <div className="hof-stat__label">approved spots</div>
          </div>
          <div className="hof-stat">
            <div className="hof-stat__n" data-testid="hof-total-anglers">
              {data?.leaderboard?.length ?? "—"}
            </div>
            <div className="hof-stat__label">contributors</div>
          </div>
          <div className="hof-stat">
            <div className="hof-stat__n">
              {data?.top_species?.length ?? "—"}
            </div>
            <div className="hof-stat__label">species mapped</div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <div className="hof-filters" data-testid="hof-filters">
        <select
          value={water}
          onChange={(e) => setWater(e.target.value)}
          className="hof-select"
          data-testid="hof-water"
        >
          <option value="">All waters</option>
          <option value="salt">Saltwater</option>
          <option value="fresh">Freshwater</option>
        </select>
        <select
          value={state}
          onChange={(e) => setState(e.target.value)}
          className="hof-select"
          data-testid="hof-state"
        >
          <option value="">All states</option>
          {Object.entries(STATE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* Body */}
      <main className="hof-body">
        {/* Sidebar — leaderboard */}
        <aside className="hof-side">
          <section className="hof-panel">
            <h3 className="hof-panel__h">
              <Trophy size={14}/> Top contributors
            </h3>
            {(data?.leaderboard || []).length === 0 ? (
              <p className="hof-panel__empty">No contributors yet — be the first.</p>
            ) : (
              <ol className="hof-leaderboard" data-testid="hof-leaderboard">
                {data.leaderboard.map((c, i) => (
                  <li key={i}>
                    <span className="hof-leaderboard__rank">#{i + 1}</span>
                    <span className="hof-leaderboard__name">{c.name}</span>
                    <span className="hof-leaderboard__count">{c.spots}</span>
                  </li>
                ))}
              </ol>
            )}
          </section>

          <section className="hof-panel">
            <h3 className="hof-panel__h">
              <Fish size={14}/> Most-submitted species
            </h3>
            {(data?.top_species || []).length === 0 ? (
              <p className="hof-panel__empty">No species mapped yet.</p>
            ) : (
              <ol className="hof-species" data-testid="hof-top-species">
                {data.top_species.map((s, i) => (
                  <li key={i}>
                    <Link to={`/fish-map?q=${encodeURIComponent(s.species)}`}>
                      {s.species}
                    </Link>
                    <span className="hof-species__count">{s.spots}</span>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </aside>

        {/* Main grid */}
        <section className="hof-grid-wrap">
          {loading ? (
            <div className="hof-loading"><Loader2 className="hof-spin" /> Loading…</div>
          ) : (data?.results || []).length === 0 ? (
            <div className="hof-empty">
              <Trophy size={40}/>
              <h3>The leaderboard is wide open.</h3>
              <p>No crowdsourced spots match this filter yet —
                <Link to="/fish-map"> submit yours</Link> and get the founder slot.</p>
            </div>
          ) : (
            grouped.map(([stateKey, rows]) => (
              <div className="hof-state-group" key={stateKey}>
                <h2 className="hof-state-group__h">
                  {STATE_LABEL[stateKey] || stateKey}
                  <span>{rows.length}</span>
                </h2>
                <div className="hof-grid">
                  {rows.map((r) => (
                    <article className="hof-card" key={r.id} data-testid="hof-card">
                      <div className="hof-card__top">
                        <span className={`hof-card__water hof-card__water--${r.water}`}>
                          {r.water === "fresh" ? <Droplet size={11}/> : <Waves size={11}/>}
                          {r.water === "fresh" ? "Fresh" : "Salt"}
                        </span>
                        {r.season && (
                          <span className="hof-card__season">{r.season}</span>
                        )}
                      </div>
                      <h3 className="hof-card__species">{r.species}</h3>
                      <div className="hof-card__loc">
                        <MapPin size={12} />
                        <span>
                          {r.town || r.region}
                          {r.region && r.town ? ` · ${r.region}` : ""}
                        </span>
                      </div>
                      {r.notes && <p className="hof-card__notes">{r.notes}</p>}
                      <div className="hof-card__credit">
                        Shared by <strong>{r.submitter_name}</strong>
                      </div>
                      <Link
                        to={`/fish-map?q=${encodeURIComponent(r.species)}`}
                        className="hof-card__cta"
                        data-testid="hof-card-cta"
                      >
                        See on the map →
                      </Link>
                    </article>
                  ))}
                </div>
              </div>
            ))
          )}
        </section>
      </main>

      <footer className="hof-footer">
        Hustle Fish Map · Hall of Fame · Submissions reviewed within 48 hours.
      </footer>
    </div>
  );
}
